import MuiButton, { ButtonProps as MuiButtonProps } from '@material-ui/core/Button';
import classNames from 'classnames';
import React from 'react';

import ButtonLoader from './ButtonLoader';
import useStyles from './styles';

type ButtonType = 'primary' | 'secondary' | 'text';

export interface ButtonBaseProps {
  autoWidth?: boolean;
  disableUnderline?: boolean;
  id?: string;
  isLoading?: boolean;
  keepLoadingLabel?: boolean;
  maxWidth?: string;
  negative?: boolean;
  submit?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  type?: ButtonType;
  target?: string;
}

export type ButtonProps = Partial<ButtonBaseProps> & Omit<MuiButtonProps, 'type'>;

const getVariant = (type: ButtonType): MuiButtonProps['variant'] => {
  switch (type) {
    case 'primary':
      return 'contained';
    case 'secondary':
      return 'outlined';
    case 'text':
      return 'text';
    default:
      return 'contained';
  }
};

export function Button<C extends React.ElementType>(props: MuiButtonProps<C, { component?: C }> & ButtonBaseProps) {
  const {
    children,
    className,
    disableUnderline = false,
    type = 'primary',
    submit = false,
    autoWidth = false,
    maxWidth = '100vw',
    negative = false,
    isLoading = false,
    keepLoadingLabel = false,
    variant,
    fullWidth = false,
    size = 'medium',
    ...otherProps
  } = props;

  const classes = useStyles({ autoWidth, fullWidth, type });

  return (
    <MuiButton
      classes={{ root: classes.root }}
      className={classNames(className, classes[type], classes[size], {
        [classes.autoWidth]: autoWidth,
        [classes.fullWidth]: fullWidth,
        [classes.maxWidth]: maxWidth,
        [classes.error]: negative,
        [classes.disableUnderline]: disableUnderline,
      })}
      type={submit ? 'submit' : 'button'}
      variant={variant ?? getVariant(type)}
      aria-busy={isLoading || undefined}
      {...otherProps}
    >
      {/* The label stays in the tree while loading so the button keeps its
          translated name; FDS has no string of its own to give it. */}
      {isLoading ? (
        <>
          <ButtonLoader />
          <span className={keepLoadingLabel ? classes.loadingLabel : classes.visuallyHidden}>{children}</span>
        </>
      ) : (
        children
      )}
    </MuiButton>
  );
}

export default Button;
